import Abort from 'utils/abort'
import { sentryLog } from 'utils/sentryLogging'

interface Options {
  method?: string
  headers?: { [key: string]: string }
  body?: BodyInit
  params?: object
}

const getRequestHeaders = (headers: Options['headers']) => {
  if (headers) {
    const requestHeaders = new Headers()

    Object.entries(headers).forEach(([key, value]) => {
      requestHeaders.set(key, value)
    })

    return requestHeaders
  }

  return headers
}

const handleError = (
  error: Error,
  {
    requestUrl,
    requestConfig,
    data,
  }: {
    requestUrl: URL
    requestConfig: RequestInit
    data: any
  }
) => {
  const cause = error?.cause as unknown as
    | ({ response: Response } & Pick<Response, 'status' | 'statusText'>)
    | undefined

  sentryLog(error, {
    url: requestUrl.toString(),
    cause,
    requestConfig,
    response: cause?.response,
    responseDataError: data?.error,
  })

  throw error
}

const fetchJson = async (url: string, options: Options): Promise<any> => {
  const { method, headers, body, params }: Options = options
  const init: RequestInit = {
    credentials: 'include',
    signal: Abort.getInstance().signal,
  }

  const requestUrl = new URL(url)

  if (method) {
    init.method = method
  }

  const requestHeaders = getRequestHeaders(headers)

  if (requestHeaders) {
    init.headers = requestHeaders
  }

  if (body) {
    init.body = body
  }

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      requestUrl.searchParams.set(key, value)
    })
  }

  let response: Response
  let data: any

  try {
    response = await fetch(requestUrl.toString(), init)

    const cause = {
      response,
      status: response.status,
      statusText: response.statusText,
    }

    if (!response.ok) {
      throw new Error(
        `Failed to fetch response: ${response.status} ${response.statusText}`,
        {
          cause,
        }
      )
    }

    data = await response.json()

    if (data?.error) {
      throw new Error(data.error?.message || 'Failed to fetch data', {
        cause,
      })
    }

    return data
  } catch (e) {
    const error = e as Error

    return handleError(error, {
      requestUrl,
      requestConfig: init,
      data,
    })
  }
}

export default fetchJson
